import type {
  ConclusionType,
  FullAssociationConclusionType,
  LegacyConclusionType,
  RelationshipLineageType,
} from '@fs/zion-tree-types'
import type {
  AlternateNameType,
  ChineseAlternateNameType,
  ConclusionPropertiesType,
  PropertyType,
} from '../types/conclusion'

export const LINEAGE_TYPES: RelationshipLineageType[] = ['ADOPTIVE', 'BIOLOGICAL', 'FOSTER', 'LEGAL_GUARDIAN', 'STEP']

export const ALTERNATE_NAME_TYPES: AlternateNameType[] = ['AKA', 'BIRTH', 'MARRIED', 'NICK']

export const CHINESE_ALTERNATE_NAME_TYPES: ChineseAlternateNameType[] = ['COURTESY', 'ART', 'TABOO', 'POSTHUMOUS']

export const ASSOCIATION_TYPES: FullAssociationConclusionType[] = [
  'ENSLAVEMENT',
  'EMANCIPATION',
  'GENERATION',
  'RELATION',
  'NEIGHBORHOOD',
  'HOUSEHOLD',
  'GODPARENTHOOD',
  'EMPLOYMENT',
  'APPRENTICESHIP',
  'SLAVERY',
]

export const relationshipConclusionCategories = ['parentChildRelationshipEvent', 'coupleEvent', 'coupleFact'] as const

export const changelogTypeMap = {
  AFFILIATION: 'affiliation',
  BAR_MITZVAH: 'bar-mitzvah',
  BAS_MITZVAH: 'bas-mitzvah',
  CREMATION: 'cremation',
  IMMIGRATION: 'immigration',
  MILITARY_SERVICE: 'military-service',
  NATURALIZATION: 'naturalization',
  NOBILITY_TITLE: 'nobility-title',
  OCCUPATION: 'occupation',
  RELIGIOUS_AFFILIATION: 'religious-affiliation',
  RESIDENCE: 'residence',
  STILLBORN: 'stillborn',
  ALTERNATE_NAME: 'alternate-name',
  ANNULMENT: 'couple',
  BIRTH: 'birth',
  BURIAL: 'burial',
  CASTE_NAME: 'caste-name',
  CHRISTENING: 'christening',
  CLAN_NAME: 'clan-name',
  COMMON_LAW_MARRIAGE: 'couple',
  COUPLE_NEVER_HAD_CHILDREN: 'couple',
  DEATH: 'death',
  DIVORCE: 'couple',
  GENDER: 'sex',
  HISTORICAL_FACT: 'historical-fact',
  LIVED_TOGETHER: 'couple',
  MARRIAGE: 'couple',
  NAME: 'preferred-name',
  NATIONAL_ID: 'national-id',
  NATIONAL_ORIGIN: 'national-origin',
  NO_COUPLE_RELATIONSHIPS: 'no-couple-relationships',
  NO_CHILDREN: 'no-children',
  PHYSICAL_DESCRIPTION: 'physical-description',
  RACE: 'race',
  TRIBE_NAME: 'tribe-name',
  PARENT1_LINEAGE: 'parent-child',
  PARENT2_LINEAGE: 'parent-child',
  LIFE_SKETCH: 'life-sketch',
  SLAVERY: 'slavery',
  ENSLAVEMENT: 'enslavement',
  EMANCIPATION: 'emancipation',
  NEIGHBORHOOD: 'neighborhood',
  HOUSEHOLD: 'household',
  GODPARENTHOOD: 'godparenthood',
  EMPLOYMENT: 'employment',
  APPRENTICESHIP: 'apprenticeship',
  RELATION: 'relation',
  GENERATION: 'generation',
  OTHER_EVENT: 'other-event',
  OTHER_FACT: 'other-fact',
} as const satisfies Record<ConclusionType | 'SLAVERY' | LegacyConclusionType, string>

const associationType = {
  fields: ['description', 'date', 'place', 'justification'],
  focusRefField: 'date',
  detailsType: 'AssociationDetails',
  category: 'associationEvent',
  requiredFields: ['description'],
  multiValued: true,
  deletable: true,
  addOrEditSuggestedReasonsType: 'changeOtherRelationship',
  deleteSuggestedReasonsType: 'deleteOtherRelationship',
} satisfies PropertyType

export const CONCLUSION_PROPERTIES_MAP = {
  AFFILIATION: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  BAR_MITZVAH: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  BAS_MITZVAH: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  CREMATION: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  IMMIGRATION: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description', 'place'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  MILITARY_SERVICE: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description', 'place'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  NATURALIZATION: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description', 'place'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  NOBILITY_TITLE: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  OCCUPATION: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  RELIGIOUS_AFFILIATION: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  RESIDENCE: {
    fields: ['date', 'place', 'justification', 'description'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['description', 'place'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  STILLBORN: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  ALTERNATE_NAME: {
    fields: ['nameTemplate', 'nameType', 'justification', 'template'],
    focusRefField: 'nameTemplate',
    detailsType: 'NameDetails',
    category: 'otherFact',
    requiredFields: ['nameTemplate'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  ANNULMENT: {
    fields: ['date', 'place', 'justification', 'coupleEventType'],
    focusRefField: 'coupleEventType',
    detailsType: 'EventDetails',
    category: 'coupleEvent',
    requiredFields: [],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },

  BIRTH: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'vitalEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteVital',
  },
  BURIAL: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'vitalEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteVital',
  },
  CASTE_NAME: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  CHRISTENING: {
    fields: ['date', 'place', 'justification'],
    focusRefField: 'date',
    detailsType: 'EventDetails',
    category: 'vitalEvent',
    requiredFields: ['date', 'place'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteVital',
  },
  CLAN_NAME: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  COMMON_LAW_MARRIAGE: {
    fields: ['date', 'place', 'justification', 'coupleEventType'],
    focusRefField: 'coupleEventType',
    detailsType: 'EventDetails',
    category: 'coupleEvent',
    requiredFields: [],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeCoupleEvent',
    deleteSuggestedReasonsType: 'deleteCoupleEvent',
  },
  COUPLE_NEVER_HAD_CHILDREN: {
    fields: ['justification', 'coupleFactType'],
    focusRefField: 'justification',
    detailsType: 'CharacteristicDetails',
    category: 'coupleFact',
    requiredFields: [],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeCoupleFact',
  },

  DEATH: {
    fields: ['living', 'date', 'place', 'justification'],
    focusRefField: null,
    detailsType: 'EventDetails',
    category: 'vitalEvent',
    requiredFields: [],
    multiValued: false,
    deletable: false,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteVital',
  },
  DIVORCE: {
    fields: ['date', 'place', 'justification', 'coupleEventType'],
    focusRefField: 'coupleEventType',
    detailsType: 'EventDetails',
    category: 'coupleEvent',
    requiredFields: [],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeCoupleEvent',
    deleteSuggestedReasonsType: 'deleteCoupleEvent',
  },
  GENDER: {
    fields: ['sex', 'justification'],
    focusRefField: null,
    detailsType: 'GenderDetails',
    category: 'vitalFact',
    requiredFields: ['sex'],
    multiValued: false,
    deletable: false,
    addOrEditSuggestedReasonsType: 'addOrChangeSex',
    deleteSuggestedReasonsType: 'deleteVital',
  },

  // @deprecated TODO this is not used anymore; remove
  HISTORICAL_FACT: {
    fields: [
      'title',
      'description',
      'date',
      // 'place', potentially will be supported in the future, but is not currently supported in Nov 2019
      'url',
      'urlTitle',
    ],
    focusRefField: null,
    detailsType: undefined,
    category: 'historicalFact',
    requiredFields: [],
    multiValued: true,
    deletable: true,
  },

  LIVED_TOGETHER: {
    fields: ['date', 'place', 'justification', 'coupleEventType'],
    focusRefField: 'coupleEventType',
    detailsType: 'EventDetails',
    category: 'coupleEvent',
    requiredFields: [],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeCoupleEvent',
    deleteSuggestedReasonsType: 'deleteCoupleEvent',
  },
  MARRIAGE: {
    fields: ['date', 'place', 'justification', 'coupleEventType'],
    focusRefField: 'coupleEventType',
    detailsType: 'EventDetails',
    category: 'coupleEvent',
    requiredFields: [],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeCoupleEvent',
    deleteSuggestedReasonsType: 'deleteCoupleEvent',
  },

  NAME: {
    fields: ['nameTemplate', 'justification', 'template'],
    focusRefField: 'nameTemplate',
    detailsType: 'NameDetails',
    category: 'vitalFact',
    requiredFields: ['nameTemplate'],
    multiValued: false,
    deletable: false,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteVital',
  },
  NATIONAL_ID: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  NATIONAL_ORIGIN: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },

  NO_COUPLE_RELATIONSHIPS: {
    fields: ['justification'],
    focusRefField: 'justification',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: [],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  NO_CHILDREN: {
    fields: ['justification'],
    focusRefField: 'justification',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: [],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },

  PHYSICAL_DESCRIPTION: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  RACE: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: false,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },

  TRIBE_NAME: {
    fields: ['justification', 'description'],
    focusRefField: 'description',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['description'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  PARENT1_LINEAGE: {
    fields: ['date', 'justification', 'type'],
    focusRefField: 'type',
    detailsType: 'EventDetails',
    category: 'parentChildRelationshipEvent',
    requiredFields: ['type'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeParentEvent',
  },
  PARENT2_LINEAGE: {
    fields: ['date', 'justification', 'type'],
    focusRefField: 'type',
    detailsType: 'EventDetails',
    category: 'parentChildRelationshipEvent',
    requiredFields: ['type'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangeParentEvent',
  },
  LIFE_SKETCH: {
    fields: ['text', 'justification', 'certified'],
    focusRefField: 'text',
    detailsType: 'LifeSketchDetails',
    category: 'lifesketch',
    requiredFields: ['text'],
    multiValued: false,
    deletable: true,
  },
  SLAVERY: { ...associationType, fields: [...associationType.fields, 'event'] },
  // Enslavement and emancipation types are only used to prevent getting an invalid conclusion type
  ENSLAVEMENT: { ...associationType, fields: [...associationType.fields, 'event'] },
  EMANCIPATION: { ...associationType, fields: [...associationType.fields, 'event'] },
  NEIGHBORHOOD: associationType,
  HOUSEHOLD: associationType,
  GODPARENTHOOD: associationType,
  EMPLOYMENT: associationType,
  APPRENTICESHIP: associationType,
  RELATION: { ...associationType, fields: ['description'], focusRefField: 'description' },
  GENERATION: {
    ...associationType,
    fields: ['description', 'generationNumber', 'justification'],
    focusRefField: 'description',
  },
  // Keep OTHER_EVENT and OTHER_FACT at end of this list.
  OTHER_EVENT: {
    fields: ['title', 'date', 'place', 'justification', 'description'],
    focusRefField: 'title',
    detailsType: 'EventDetails',
    category: 'otherEvent',
    requiredFields: ['title'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
  OTHER_FACT: {
    fields: ['title', 'description', 'justification'],
    focusRefField: 'title',
    detailsType: 'CharacteristicDetails',
    category: 'otherFact',
    requiredFields: ['title'],
    multiValued: true,
    deletable: true,
    addOrEditSuggestedReasonsType: 'addOrChangePersonConclusion',
    deleteSuggestedReasonsType: 'deleteNonVital',
  },
} satisfies ConclusionPropertiesType
